import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Divider, 
  CircularProgress, Alert, AlertTitle, Card, CardContent, Table, TableRow, TableCell, TableHead, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import primaryBackground from "../assets/Backgrounds/primaryBg.svg";
import Logo from "../imges/logos/mindsinaction/logo.png";
import { useAuth } from "../auth/AuthContext";

const companyDetails = {
  name: "Mindsinaction STEAM CENTER.",
  email: "admin@mindsinaction.com.na",
  cell: "+264 81 363 0529",
  address: "Toivo ya Toivo No.6. Suiderhof, Windhoek, Huster Machinetoolbuilding First Floor"
};

const Receipt = ({ paymentData, findingStudent, onClose }) => {
  const receiptRef = useRef(null);
  const [hideButton, setHideButton] = useState(false);

  const generateReceiptNumber = () => {
    return `INV-${Math.floor(100000 + Math.random() * 900000)}`;
  };

  const receiptNumber = generateReceiptNumber();
  const currentDate = new Date().toLocaleDateString();

  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load logo image'));
      img.src = src;
    });
  };

  const handleDownloadPDF = async () => {
    console.log('handleDownloadPDF started');
    try {
      setHideButton(true);
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Preload logo to avoid CORS issues
      await preloadImage(Logo);

      // Temporarily move to body to handle Dialog portal
      const originalParent = receiptRef.current.parentNode;
      if (originalParent) {
        document.body.appendChild(receiptRef.current);
        receiptRef.current.style.position = 'absolute';
        receiptRef.current.style.left = '-9999px';
      }

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: receiptRef.current.scrollWidth,
        height: receiptRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: document.body.scrollWidth,
        windowHeight: document.body.scrollHeight,
        logging: false,
      });

      console.log('Canvas created:', canvas.width, 'x', canvas.height);
      if (originalParent) {
        originalParent.appendChild(receiptRef.current);
        receiptRef.current.style.position = '';
        receiptRef.current.style.left = '';
      }

      const imgData = canvas.toDataURL("image/png");
      if (imgData.length < 100) {
        throw new Error('Canvas is blank - check logo CORS or Dialog visibility');
      }

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      const x = (pdfWidth - scaledWidth) / 2; // Center horizontally
      const y = 0; // Start from top

      doc.addImage(imgData, "PNG", x, y, scaledWidth, scaledHeight);
      doc.save(`receipt_${receiptNumber}.pdf`);
      console.log('PDF saved successfully');
    } catch (error) {
      console.error('Full PDF error:', error);
      alert(`PDF failed: ${error.message}. See console for details.`);
    } finally {
      setHideButton(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { width: { xs: '90%', sm: '70%', md: '50%' }, maxWidth: '600px' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Payment Receipt</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Card
          ref={receiptRef}
          sx={{ 
            mb: 2, 
            backgroundColor: "#ffffff", 
            width: { xs: '100%', sm: 400, md: 500 }, // Fixed width for PDF consistency
            mx: 'auto', // Center the card
            p: 2,
            boxSizing: 'border-box',
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ mb: { xs: 2, sm: 0 } }}>
                <img src={Logo} alt="Company Logo" style={{ width: 120, height: "auto" }} />
              </Box>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, maxWidth: { xs: '100%', sm: 200 } }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {companyDetails.name}
                </Typography>
                <Typography variant="subtitle2">Email: {companyDetails.email}</Typography>
                <Typography variant="subtitle2">Cell: {companyDetails.cell}</Typography>
                <Typography variant="subtitle2">{companyDetails.address}</Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Typography variant="h6" gutterBottom>
                Payment Receipt
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, sm: 0 } }}>
                <Typography>STATUS: </Typography>
                <Typography color="green">PAID</Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                <strong>TO:</strong> {paymentData.customerName || 'N/A'}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Receipt Number:</strong> {receiptNumber}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Date:</strong> {currentDate}
              </Typography>
              {findingStudent && (
                <Typography variant="subtitle1">
                  <strong>Reference Number:</strong> {findingStudent.trans_Ref || 'N/A'}
                </Typography>
              )}
            </Box>

            <Typography variant="h6" gutterBottom>
              Transaction Details
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#3f51b5", color: "#ffffff" }}>
                    Description
                  </TableCell>
                  <TableCell align="right" sx={{ backgroundColor: "#3f51b5", color: "#ffffff" }}>
                    Amount (N$)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Payment Amount</TableCell>
                  <TableCell align="right">{paymentData.amount || "0.00"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box sx={{ mt: 2 , borderTop: '2px solid #57b177'}}>
              <Box sx={{backgroundColor: '#57b177', width: '130px', padding: '.2rem', float: 'right'}}>
                <Typography> <strong>TOTAL</strong> N$ {paymentData.amount || "0.00"}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
        <Button 
          onClick={handleDownloadPDF}
          variant="contained" 
          color="primary"
          disabled={hideButton}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {hideButton ? <CircularProgress size={20} /> : 'Download PDF'}
        </Button>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PaymentSuccess = () => {
  const [paymentData, setPaymentData] = useState(null);
  const [findingStudent, setFindingStudent] = useState(null);
  const [studentData, setStudentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getData, updateData } = useAuth();

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const transactionToken = params.get('ID');
        
        if (!transactionToken) {
          throw new Error('Transaction token not found in URL');
        }

        const formData = new FormData();
        formData.append("transToken", transactionToken);

        const response = await axios.post(
          "https://mindsinaction.com.na/api/dpo/verify_token.php",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
          }
        );
        
        if (response.data.success) {
          setPaymentData(response.data.data);
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [location.search]);

  useEffect(() => {
    const fetchStudentData = async () => {
      if (paymentData && paymentData.transactionToken) {
        try {
          const studentResponse = await getData('proccess.php', 'students_V2');
          if (studentResponse.success) {
            setStudentData(studentResponse.data);
          }
        } catch (err) {
          console.error('Error fetching student data:', err);
          setError(err.message);
        }
      }
    };

    fetchStudentData();
  }, [paymentData, getData]);

  useEffect(() => {
    const matchStudent = () => {
      if (studentData.length > 0 && paymentData && paymentData.transactionToken) {
        const matchedStudent = studentData.find(
          (student) => student.trans_token === paymentData.transactionToken
        );
        if (matchedStudent) {
          setFindingStudent(matchedStudent);
        } else {
          console.log('No student matched for transactionToken:', paymentData.transactionToken);
        }
      }
    };

    matchStudent();
  }, [studentData, paymentData]);

  useEffect(() => {
    const updateStudentData = async () => {
      if (findingStudent && paymentData && paymentData.transactionToken) {
        try {
          const studentResponse = await updateData('proccess.php', 'students_V2', {
            trans_token: paymentData.transactionToken,
            trans_status: 0,
            trans_result_explanation: paymentData.status,
            dueToUs: 0,
            status: 1
          }, findingStudent.id);
          if (studentResponse.success) {
            setStudentData(studentResponse.data);
          }
        } catch (err) {
          console.error('Error updating student data:', err);
          setError(err.message);
        }
      }
    };

    updateStudentData();
  }, [findingStudent, paymentData, updateData]);

  const handleReturnHome = () => {
    navigate('/');
  };

  const handleViewReceipt = () => {
    setShowReceipt(true);
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${primaryBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ width: { xs: '90%', sm: '70%', md: '50%' }, maxWidth: '600px', mx: 'auto', p: { xs: 2, sm: 3 } }}>
          <Alert severity="error">
            <AlertTitle>Payment Verification Failed</AlertTitle>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            startIcon={<HomeIcon />}
            onClick={handleReturnHome}
            sx={{ mt: 2, width: { xs: '100%', sm: 'auto' } }}
          >
            Return Home
          </Button>
        </Box>
      </Box>
    );
  }

  if (!paymentData) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${primaryBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: { xs: '90%', sm: '70%', md: '50%' }, maxWidth: '600px', mx: 'auto', p: { xs: 2, sm: 3 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, backgroundColor: '#ffffffff' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: 'success.main' }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Payment Successful
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Thank you for your payment!
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Transaction Details
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}>
              <Typography>Amount Paid:</Typography>
              <Typography fontWeight="bold">
                {paymentData.currency} {paymentData.amount}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}>
              <Typography>Date:</Typography>
              <Typography>
                {new Date(paymentData.date).toLocaleString()}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}>
              <Typography>Status:</Typography>
              <Typography color="success.main" fontWeight="bold">
                {paymentData.status}
              </Typography>
            </Box>

            {findingStudent ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}>
                <Typography>Reference Number:</Typography>
                <Typography fontWeight="bold">{findingStudent.trans_Ref || 'N/A'}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}>
                <Typography>Student Transaction Ref:</Typography>
                <Typography fontWeight="bold" color="text.secondary">
                  No student matched
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleReturnHome}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Return Home
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ReceiptIcon />}
              onClick={handleViewReceipt}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              View Receipt
            </Button>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            A confirmation email has been sent to your registered email address.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            For any questions, please contact support@mindsinaction.com.na
          </Typography>
        </Box>

        {showReceipt && (
          <Receipt 
            paymentData={paymentData} 
            findingStudent={findingStudent}
            onClose={handleCloseReceipt}
          />
        )}
      </Box>
    </Box>
  );
};

export default PaymentSuccess;